import {
  Fieldset,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  type DatesRangeValue,
  DateValue,
} from '@mantine/dates/lib/types/DatePickerValue';
import { showNotification } from '@mantine/notifications';
import { IconX } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { cloneDeep } from 'lodash';
import { useMemo, useState } from 'react';
import * as React from 'react';

import CustomMonthPickerInput from '../../../components/CustomMonthPickerInput';
import Page from '../../../components/Page/Page';
import { useAuth } from '../../../contexts/AuthProvider';
import { useI18n } from '../../../contexts/I18nProvider';
import useCompany from '../../../hooks/useCompany';
import useCompanyModules from '../../../hooks/useCompanyModules';
import useFetchDivisions from '../../../hooks/useFetchDivisions';
import EndOfMonthService from '../../../services/EndOfMonthService';
import PayslipService from '../../../services/PayslipService';
import {
  ModuleName,
  SynchroOpenpayeParams,
} from '../../../types/api/response/module';
import { BuiltInRoles } from '../../../types/api/response/role';
import { PayslipMonthResponseFront } from '../../../types/types';
import { hasRole } from '../../../utils/authorization';
import { toDateOfDayString } from '../../../utils/format';
import handleErrorMessage from '../../../utils/handleErrorMessage';
import { initPickerDate } from '../../../utils/initPickerDate';
import PayslipsStatusTabs from './components/PayslipsStatusTabs';
import PayslipOcr from './ocr/PayslipOcr';

type Props = {
  accountantCompanyId?: string;
  customerCompanyId?: string;
  customerSelectedDate?: DateValue | DatesRangeValue | Date[];
};

export default function EndOfMonthPayslips({
  accountantCompanyId,
  customerCompanyId,
  customerSelectedDate,
}: Props) {
  const { t } = useI18n();
  const { user } = useAuth();
  const isEmbedded = Boolean(accountantCompanyId || customerCompanyId);
  const { id: companyIdCustomer, company } = useCompany(
    user,
    customerCompanyId
  );
  const { id: companyIdAccountant } = useCompany(user, accountantCompanyId);
  const { getModule } = useCompanyModules(companyIdAccountant);
  const moduleOpenpaye = getModule(ModuleName.SynchroOpenPaye);
  const moduleOpenpayeParams = moduleOpenpaye?.configuration
    ?.params as SynchroOpenpayeParams;
  const [selectedDate, setSelectedDate] = useState<
    DateValue | DatesRangeValue | Date[]
  >(customerSelectedDate ? customerSelectedDate : initPickerDate());
  const { divisions } = useFetchDivisions({ companyId: companyIdCustomer });

  // Le type du picker couvre aussi ses modes range et multiple, mais CustomMonthPickerInput ne
  // renvoie qu'une date simple.
  const selectedMonth = dayjs(selectedDate as DateValue).month() + 1;
  const selectedYear = dayjs(selectedDate as DateValue).year();

  // La période fait partie de la clé : changer de mois refetche, et l'invalidation émise après
  // une correction de bulletin dans la revue OCR rafraîchit les listes sans navigation.
  const {
    data: payslipsMonthInfoData,
    isLoading,
    isFetching,
    isSuccess,
  } = useQuery({
    enabled: Boolean(companyIdCustomer),
    queryKey: [
      'PayslipService.getPayslipsMonthInfo',
      companyIdCustomer,
      selectedMonth,
      selectedYear,
    ],
    queryFn: () =>
      PayslipService.getPayslipsMonthInfo(
        companyIdCustomer,
        selectedMonth,
        selectedYear
      ),
  });

  const { mutate: synchroOpenpayePayslips } = useMutation({
    mutationFn: () =>
      EndOfMonthService.synchroOpenpayePayslips(
        companyIdAccountant,
        companyIdCustomer,
        selectedMonth,
        selectedYear
      ),
    onSuccess: () => {
      showNotification({
        id: 'success-synchroOpenpayePayslips',
        message: t('w.success'),
        color: 'green',
        icon: <IconX />,
      });
    },
    onError: (error) => {
      showNotification({
        id: 'error-synchroOpenpayePayslips',
        title: t('w.error'),
        message: handleErrorMessage(error, t),
        color: 'red',
        icon: <IconX />,
      });
    },
  });

  const payslipsMonthInfo: PayslipMonthResponseFront | undefined =
    useMemo(() => {
      if (!payslipsMonthInfoData) {
        return undefined;
      }

      const _payslipsMonthInfoData: PayslipMonthResponseFront = cloneDeep(
        payslipsMonthInfoData
      );

      _payslipsMonthInfoData.ok = _payslipsMonthInfoData.ok.map(
        (userPayslip) => ({
          ...userPayslip,
          division: cloneDeep(
            divisions?.find((elt) => elt.id === userPayslip.divisionId)
          ),
        })
      );

      _payslipsMonthInfoData.notFound = _payslipsMonthInfoData.notFound.map(
        (userPayslip) => ({
          ...userPayslip,
          division: cloneDeep(
            divisions?.find((elt) => elt.id === userPayslip.divisionId)
          ),
        })
      );

      _payslipsMonthInfoData.new = _payslipsMonthInfoData.new.map(
        (userPayslip) => ({
          ...userPayslip,
          division: cloneDeep(
            divisions?.find((elt) => elt.id === userPayslip.divisionId)
          ),
        })
      );

      if (
        !hasRole(BuiltInRoles.ADMINISTRATOR, user, companyIdCustomer) &&
        !hasRole(BuiltInRoles.DIRECTION, user, companyIdCustomer)
      ) {
        _payslipsMonthInfoData.ok = _payslipsMonthInfoData.ok.filter(
          (item) =>
            item.division?.n1 === user.id ||
            item.division?.n2 === user.id ||
            item.division?.assistant === user.id
        );
        _payslipsMonthInfoData.notFound =
          _payslipsMonthInfoData.notFound.filter(
            (item) =>
              item.division?.n1 === user.id ||
              item.division?.n2 === user.id ||
              item.division?.assistant === user.id
          );
        _payslipsMonthInfoData.new = _payslipsMonthInfoData.new.filter(
          (item) =>
            item.division?.n1 === user.id ||
            item.division?.n2 === user.id ||
            item.division?.assistant === user.id
        );
      }

      return _payslipsMonthInfoData;
    }, [payslipsMonthInfoData, divisions]);

  return (
    <Page
      title={t('w.payslips')}
      parent={{ label: company ? company.name : toDateOfDayString() }}
      withBackButton={!isEmbedded}
      links={
        Boolean(moduleOpenpaye?.active) && Boolean(moduleOpenpayeParams)
          ? [
              {
                active: false,
                label: t('openpaye.getPayslips'),
                onClick: () => {
                  showNotification({
                    id: 'loading-synchroOpenpayePayslips',
                    message: t('w.isLoading'),
                    color: 'green',
                    icon: <IconX />,
                  });
                  synchroOpenpayePayslips();
                },
              },
            ]
          : undefined
      }
      headerRightSection={
        <Group gap={'sm'}>
          <CustomMonthPickerInput
            key={'month-picker-end-of-month-payslips'}
            value={selectedDate}
            onChange={setSelectedDate}
          />
        </Group>
      }
    >
      {/* L'extraction OCR est le seul import : rendue en place, sans passer par sa route dédiée
          (qui reste servie pour les liens directs). Embarquée dans la vue gestionnaire, elle est
          scopée sur l'entreprise cliente au lieu de celle de l'utilisateur connecté. */}
      <Fieldset
        mt="md"
        legend={
          <Text size="xl" fw="bold">
            {t('w.import')}
          </Text>
        }
      >
        <PayslipOcr
          inline
          customerCompanyId={isEmbedded ? companyIdCustomer : undefined}
          onImported={setSelectedDate}
        />
      </Fieldset>
      {/* L'overlay est scopé aux résultats : un refetch (changement de mois, correction d'un
          bulletin dans la revue OCR) ne doit pas masquer l'import juste au-dessus. */}
      <Fieldset
        pos="relative"
        variant="filled"
        mt="md"
        legend={
          <Text size="xl" fw="bold">
            {t('w.importResult')}
          </Text>
        }
      >
        <LoadingOverlay visible={isLoading || isFetching} />
        <Stack>
          {!isFetching && !isLoading && isSuccess ? (
            <PayslipsStatusTabs
              companyId={companyIdCustomer}
              payslipsMonthInfo={payslipsMonthInfo}
              selectedDate={selectedDate}
            />
          ) : (
            <Stack align={'center'} mt={'xl'} gap={'md'}>
              <Title size={'h3'} c={'hifivework.3'}>
                {t('w.noPayslipsImported')}
              </Title>
            </Stack>
          )}
        </Stack>
      </Fieldset>
    </Page>
  );
}
